import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { base as duiBase } from "@dui/primitives/core";
import { type Session, supabase } from "../supabase.ts";
import { pageOf } from "../routes.ts";
import { type SignInEvent } from "./sign-in-form.ts";

type SignIn = {
  readonly submitting: boolean;
  readonly error: string | undefined;
};

/**
 * Take the error Supabase Auth puts on the redirect when Google sign-in fails
 * (e.g. "Signups not allowed for this instance"), and drop it from the URL so
 * a reload doesn't show it again.
 */
const takeRedirectError = (): string | undefined => {
  const url = new URL(globalThis.location.href);
  const error = url.searchParams.get("error_description") ?? undefined;
  if (error === undefined) return undefined;
  for (const key of ["error", "error_code", "error_description"]) {
    url.searchParams.delete(key);
  }
  globalThis.history.replaceState(globalThis.history.state, "", url);
  return error;
};

/**
 * The root of every page: the sign-out page for `/sign-out`; otherwise the
 * sign-in form until there is a session, then the page for the URL. Google
 * sign-in returns to the exact URL the form stood on, so the consent page's
 * `authorization_id` survives sign-in. Signing out anywhere below brings the
 * form back on the same URL, so a person can sign in as someone else.
 */
@customElement("accounts-root")
export class AccountsRoot extends LitElement {
  static override styles = [
    duiBase,
    css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100dvh;
        padding: var(--space-4);
        box-sizing: border-box;
        font-family: var(--font-sans);
      }
      .hint {
        color: var(--muted-foreground);
        font-size: var(--text-sm);
      }
    `,
  ];

  #page = pageOf(globalThis.location.pathname);

  /** `loading` until Supabase reports the initial session. */
  @state()
  accessor #session: Session | undefined | "loading" = "loading";

  @state()
  accessor #signIn: SignIn = { submitting: false, error: takeRedirectError() };

  #unsubscribe: (() => void) | undefined;

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.#page === "sign-out") return;
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      this.#session = session ?? undefined;
    });
    // Back from Google restores this page from the bfcache mid-submit.
    const reset = (e: PageTransitionEvent): void => {
      if (e.persisted) this.#signIn = { submitting: false, error: undefined };
    };
    globalThis.addEventListener("pageshow", reset);
    this.#unsubscribe = () => {
      data.subscription.unsubscribe();
      globalThis.removeEventListener("pageshow", reset);
    };
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#unsubscribe?.();
  }

  /** On success the browser leaves for Google; only an error comes back. */
  #handleSignIn = async (_e: SignInEvent): Promise<void> => {
    this.#signIn = { submitting: true, error: undefined };
    const { origin, pathname, search } = globalThis.location;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${origin}${pathname}${search}` },
    });
    if (error !== null)
      this.#signIn = { submitting: false, error: error.message };
  };

  #signOut = (): void => {
    void supabase.auth.signOut({ scope: "local" });
  };

  override render(): TemplateResult {
    if (this.#page === "sign-out") return html`<sign-out-page></sign-out-page>`;
    const session = this.#session;
    if (session === "loading") {
      return html`<dui-spinner size="lg"></dui-spinner>`;
    }
    if (session === undefined) {
      return html`<sign-in-form
        .error=${this.#signIn.error}
        .submitting=${this.#signIn.submitting}
        @sign-in=${this.#handleSignIn}
      ></sign-in-form>`;
    }
    if (this.#page === "consent") return html`<consent-page></consent-page>`;
    return html`
      <dui-card>
        <span slot="title">Signed in to ETO</span>
        <p class="hint">As <strong>${session.user.email}</strong>.</p>
        <dui-button slot="footer" appearance="outline" @click=${this.#signOut}>
          Sign out
        </dui-button>
      </dui-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "accounts-root": AccountsRoot;
  }
}
